import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Container,
  CardContent,
  Chip,
  Grid,
} from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { endpoints } from "../../Utils/UrlEnum";
import { postData } from "../../Services/postData";
import { disableTostify, PAYMENT_TYPES } from "../../Utils/Constants";
import { sendPurchaseEvent } from "../../Utils/GTMPurchaseEvent";
import {
  loadFacebookPixel,
  sendPurchaseEventFb,
} from "../../Utils/FBQPurchaseEvent";
import {
  loadTikTokPixel,
  sendPurchaseEventTikTok,
} from "../../Utils/LoadTikTokPixel";

const PaymentStatusPage: React.FC = () => {
  const orderId = localStorage.getItem("paymentOrderId") || "";
  const paymentType = localStorage.getItem("paymentType") || "";
  const [message, setMessage] = useState<string>("");
  const [status, setStatus] = useState<string>("");

  useEffect(() => {
    getPaymentStatus();
  }, []);

  const getPaymentStatus = async () => {
    postData(endpoints.payments.getPaymentStatus + disableTostify, {
      orderId,
    }).then((response) => {
      if (response?.data) {
        const paymentStatus = response.data.status;
        setMessage(response.data.message);
        setStatus(paymentStatus);
        if (paymentStatus === "success") {
          const orderId = localStorage.getItem("processingOrderId") ?? null;
          const amount = localStorage.getItem("paymentAmount");
          const paymentData = {
            orderId,
            clientId: localStorage.getItem("processingClientId"),
            amount,
          };

          // send purchase events for Google Analytics 4
          setTimeout(() => {
            sendPurchaseEvent({
              transaction_id: orderId || "",
              value: amount ? parseFloat(amount) : 0,
              currency: "RON",
            });
          }, 1000);

          // send purcahse exents for facebook pixel
          if (process.env.REACT_APP_FACEBOOK_PIXEL_ID) {
            loadFacebookPixel(process.env.REACT_APP_FACEBOOK_PIXEL_ID);
            setTimeout(() => {
              sendPurchaseEventFb({
                value: amount ? parseFloat(amount) : 0,
                contents: [{ id: orderId || "", quantity: 1 }],
              });
            }, 1000);
          }

          // send purcahse exents for tiktok pixel
          loadTikTokPixel(process.env.REACT_APP_TIKTOK_PIXEL_ID || "");
          setTimeout(() => {
            sendPurchaseEventTikTok({
              value: amount ? parseFloat(amount) : 0,
              orderId: orderId || "",
            });
          }, 1000);

          // create the payment transaction in local database
          postData(
            endpoints.payments.createPayment + disableTostify,
            paymentData
          ).then(async (response) => {
            if (response?.data) {
              const clientId = localStorage.getItem("processingClientId");

              // if no orderId - it means the payment is for a client's subscription
              // geenrate account
              if (!orderId) {
                await postData(
                  endpoints.client.generateUserAccount + disableTostify,
                  {
                    clientId: clientId,
                  }
                );
              }
              const invoiceEndpoint = orderId
                ? `${endpoints.oblio.generateInvoice}?orderId=${orderId}`
                : `${endpoints.oblio.generateInvoice}?clientId=${clientId}`;

              postData(invoiceEndpoint, {
                amount,
              }).then((response) => {
                if (response?.data) {
                  // create client account after successful payment
                  postData(endpoints.client.generateUserAccount, {
                    clientId: clientId,
                  }).then(async () => {
                    // if there is no orderId, it means the payment is for a client's subscription, so activate the subscription
                    if (!orderId) {
                      await postData(endpoints.client.activateSubscription, {
                        clientId: clientId,
                      });
                    }
                    localStorage.removeItem("paymentOrderId");
                    localStorage.removeItem("paymentAmount");
                    localStorage.removeItem("processingOrderId");
                    localStorage.removeItem("processingClientId");
                    localStorage.removeItem("paymentType");
                  });
                }
              });
            }
          });
        }
      }
    });
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        background: "linear-gradient(135deg, #7788d5 0%, #c3c3c3 100%)",
        py: 4,
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        {/* Header with Logo */}
        <Box
          sx={{
            textAlign: "center",
            mb: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src="/sigur-la-volan.png"
            alt="Sigur La Volan"
            sx={{
              width: { xs: "90%", sm: 400, md: 500 },
              maxWidth: "100%",
              height: "auto",
              mb: { xs: 2, sm: 3 },
              boxShadow: "0 12px 48px rgba(0,0,0,0.15)",
              border: "3px solid rgba(255,255,255,0.3)",
              borderRadius: 3,
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "scale(1.02)",
                boxShadow: "0 16px 64px rgba(0,0,0,0.2)",
              },
            }}
          />
        </Box>

        <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center">
          <Grid item xs={12} sm={10} md={8}>
            <Paper
              elevation={20}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                background: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            >
              {/* Status Message */}
              <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Alert
                  severity={status === "success" ? "success" : "error"}
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.2rem" },
                    mb: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                    "& .MuiAlert-message": {
                      width: "100%",
                      textAlign: "center",
                      fontWeight: 500,
                    },
                  }}
                >
                  {message}
                </Alert>

                {/* Success Message */}
                {status === "success" && (
                  <>
                    {paymentType === PAYMENT_TYPES.SUBSCRIPTION_RENEWAL ? (
                      // Subscription Renewal Success Message
                      <Box
                        sx={{
                          mt: 3,
                          p: 3,
                          textAlign: "center",
                          background:
                            "linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(139, 195, 74, 0.1))",
                          borderRadius: 2,
                          border: "1px solid rgba(76, 175, 80, 0.2)",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: "success.main",
                            mb: 2,
                          }}
                        >
                          {Vocabulary.subscriptionRenewalSuccess}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: "text.secondary",
                            lineHeight: 1.6,
                          }}
                        >
                          {Vocabulary.subscriptionRenewalSuccessMessage}
                        </Typography>
                      </Box>
                    ) : (
                      // New Registration Success Message
                      <>
                        {localStorage.getItem("processingOrderId") && (
                          <Box
                            sx={{
                              mt: 3,
                              p: 3,
                              textAlign: "center",
                              background:
                                "linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(139, 195, 74, 0.1))",
                              borderRadius: 2,
                              border: "1px solid rgba(76, 175, 80, 0.2)",
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: "success.main",
                                mb: 1,
                              }}
                            >
                              {Vocabulary.consultantContactMessage}
                            </Typography>
                          </Box>
                        )}

                        {/* Login Credentials Section */}
                        <Box
                          sx={{
                            mt: { xs: 3, sm: 4 },
                            p: { xs: 2, sm: 3 },
                            background:
                              "linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))",
                            borderRadius: 2,
                            border: "1px solid rgba(102, 126, 234, 0.2)",
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: { xs: 2, sm: 3 },
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: "primary.main",
                              textAlign: { xs: "center", sm: "left" },
                              flex: 1,
                            }}
                          >
                            {Vocabulary.loginCredentialsSentMessage}
                          </Typography>
                          <Chip
                            label={Vocabulary.enterAccount}
                            color="primary"
                            clickable
                            onClick={() => (window.location.href = "/login")}
                            sx={{
                              fontSize: { xs: "0.8rem", sm: "0.9rem" },
                              fontWeight: 500,
                              px: { xs: 1.5, sm: 2 },
                              py: 1,
                              height: "auto",
                              cursor: "pointer",
                            }}
                          />
                        </Box>
                      </>
                    )}
                  </>
                )}

                {/* Support Information */}
                <Box
                  sx={{
                    mt: { xs: 3, sm: 4 },
                    p: { xs: 2, sm: 3 },
                    textAlign: "center",
                    background:
                      "linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))",
                    borderRadius: 2,
                    border: "1px solid rgba(102, 126, 234, 0.2)",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "primary.main",
                      mb: 1,
                    }}
                  >
                    Aveți nevoie de ajutor?
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {Vocabulary.supportContactInfo}
                  </Typography>
                  <Chip
                    label={`${Vocabulary.contact}: ${Vocabulary.supportEmail}`}
                    color="primary"
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      fontWeight: 500,
                      px: { xs: 1.5, sm: 2 },
                      py: 1,
                      height: "auto",
                    }}
                  />
                </Box>
              </CardContent>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PaymentStatusPage;
